// src/components/official/OfficialDocsAdmin.jsx
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { Banner } from "../common/Banner";
import SectionTips from "../common/SectionTips";
import {
  getOfficialDocsConfig,
  updateOfficialDocsConfig,
} from "../../services/officialDocsService";

/* ---------- UI base ---------- */
function GradientCard({ children }) {
  return (
    <div
      className="rounded-2xl p-[1.5px]"
      style={{ background: "linear-gradient(135deg,#E6F0FF 0%,#E6FBFF 100%)" }}
    >
      <div className="rounded-2xl bg-white ring-1 ring-black/5">{children}</div>
    </div>
  );
}

const Toggle = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={(e) => {
      e.stopPropagation();
      if (disabled) return;
      onChange(!checked);
    }}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition
      ${checked ? "bg-[#0488AF]" : "bg-gray-300"}
      ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${
        checked ? "translate-x-6" : "translate-x-1"
      }`}
    />
  </button>
);

/* ---------- Tarjetas / opciones ---------- */
const options = [
  {
    key: "docsMenuEnabled",
    emoji: "🗂️",
    title: "Sección completa",
    desc: 'Muestra la sección "Documentación Oficial" a los socios',
  },
  {
    key: "showAnnualReports",
    emoji: "📑",
    title: "Cuenta pública",
    desc: "Habilita la vista pública de reportes",
  },
  {
    key: "showEstatutos",
    emoji: "📜",
    title: "Estatutos",
    desc: "Habilita la vista de estatutos",
  },
  {
    key: "showCodigoEtica",
    emoji: "⚖️",
    title: "Código de ética",
    desc: "Habilita la vista de código de ética",
  },
  {
    key: "showModeloPrevencion",
    emoji: "🛡️",
    title: "Modelo de prevención del delito",
    desc: "Habilita esta vista",
  },
  {
    key: "showReglamentoInterno",
    emoji: "📘",
    title: "Reglamento interno",
    desc: "Habilita esta vista",
  },
  {
    key: "showProtocolos",
    emoji: "🧭",
    title: "Protocolos",
    desc: "Habilita la vista de protocolos",
  },
  {
    key: "showMaterialInteres",
    emoji: "📚",
    title: "Material de interés",
    desc: "Habilita la vista de material de interés",
  },
  {
    key: "showPopups",
    emoji: "🔔",
    title: "Pop-ups",
    desc: "Gestiona los pop-ups que se muestran exclusivamente a administradores",
  },
];

/* ======================================================= */
export default function OfficialDocsAdmin({
  onOpenAnnualReports,
  onOpenStatutes,
  onOpenEthics,
  onOpenCrimePrevention,
  onOpenInternalRegulation, // reglamento interno (admin)
  onOpenProtocols,          // protocolos (admin)
  onOpenMaterials,          // material de interés (admin)
  onOpenPopups,             // pop-ups (admin)
  onBack,
}) {
  const navigate = useNavigate();
  const [view, setView] = useState(null); // null => grid | key => detalle
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [cfg, setCfg] = useState({
    docsMenuEnabled: false,
    showPublicAccount: false, // compat
    showEstatutos: false,
    showCodigoEtica: false,
    showModeloPrevencion: false,
    showReglamentoInterno: false,
    showProtocolos: false,
    showAnnualReports: false,
    showMaterialInteres: false,
    showPopups: false,
  });
  const [initial, setInitial] = useState(cfg);

  useEffect(() => {
    (async () => {
      try {
        const c = await getOfficialDocsConfig();
        setCfg((prev) => ({ ...prev, ...c }));      // merge para conservar flags nuevos
        setInitial((prev) => ({ ...prev, ...c }));
      } catch (e) {
        Swal.fire("Error", String(e), "error");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasChanges = useMemo(
    () => JSON.stringify(cfg) !== JSON.stringify(initial),
    [cfg, initial]
  );

  const save = async () => {
    try {
      setSaving(true);
      // Excluir showPopups del guardado ya que no tiene switch y es solo para admin
      const { showPopups, ...configToSave } = cfg;
      const saved = await updateOfficialDocsConfig(configToSave);
      // Mantener showPopups en el estado local (no se guarda)
      const savedWithPopups = { ...saved, showPopups: cfg.showPopups };
      setCfg(savedWithPopups);
      setInitial(savedWithPopups);

      window.dispatchEvent(new Event("official-docs-config-updated"));
      await Swal.fire({
        icon: "success",
        title: "Guardado",
        timer: 900,
        showConfirmButton: false,
      });
      window.location.reload();
    } catch (e) {
      Swal.fire("Error", String(e), "error");
    } finally {
      setSaving(false);
    }
  };

  const reset = () => setCfg(initial);

  const goBack = () => {
    if (typeof onBack === "function") return onBack();
    if (window.history.length > 1) return navigate(-1);
    navigate("/profile");
  };

  /* ---------- Vista de detalle ---------- */
  const activeOpt = options.find((o) => o.key === view);

  const Detail = () =>
    !activeOpt ? null : (
      <motion.div
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.4 }}
        className="space-y-6"
      >
        <button
          onClick={() => setView(null)}
          className="inline-flex items-center gap-3 px-3 py-2 bg-[#DC3465] hover:bg-[#c02e59] rounded-xl text-white"
        >
          <span className="text-lg">⬅</span> Volver al menú principal
        </button>

        <GradientCard>
          <div className="p-6 space-y-5">
            <p className="text-sm text-gray-600">{activeOpt.desc}</p>

            {/* Enlace al manual de administración */}
            <div className="rounded-xl border bg-[#E5F4FA] border-[#7cc3dd] px-4 py-3 flex items-center justify-between gap-3">
              <div className="text-xs text-[#075985]">
                <div className="font-semibold mb-0.5">Manual de Administración</div>
                <div className="text-[11px]">
                  Revisa el detalle completo de esta sección en el manual de administración de la plataforma RDS.
                </div>
              </div>
              <button
                type="button"
                onClick={() => window.open(
                  "https://res.cloudinary.com/duz01skao/image/upload/v1766412946/Manual_de_usuario_administraci%C3%B3n_bsvsyl.pdf",
                  "_blank"
                )}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#0488AF] text-white text-xs font-semibold hover:bg-[#03779C] transition-colors"
              >
                Abrir manual
              </button>
            </div>

            {/* Toggle - Oculto para pop-ups ya que son solo para administradores */}
            {activeOpt.key !== "showPopups" && (
              <div className="rounded-xl border p-4 flex items-center justify-between gap-4">
                <div className="text-sm">
                  <div className="font-medium">Visible para socios</div>
                  <div className="text-gray-500">
                    {activeOpt.key === "docsMenuEnabled"
                      ? "Si está apagado, la sección completa se oculta, aunque los ítems estén encendidos."
                      : "Requiere que la sección completa esté encendida."}
                  </div>
                </div>
                <Toggle
                  checked={!!cfg[activeOpt.key]}
                  disabled={activeOpt.key !== "docsMenuEnabled" && !cfg.docsMenuEnabled}
                  onChange={(v) => setCfg((c) => ({ ...c, [activeOpt.key]: v }))}
                />
              </div>
            )}
            
            {/* Información para pop-ups */}
            {activeOpt.key === "showPopups" && (
              <div className="rounded-xl border p-4 bg-blue-50">
                <div className="text-sm">
                  <div className="font-medium text-blue-900">Solo para administradores</div>
                  <div className="text-blue-700 mt-1">
                    Los pop-ups se muestran exclusivamente a usuarios con rol de administrador. Los socios no verán pop-ups.
                  </div>
                </div>
              </div>
            )}

            {/* Acciones específicas */}
            {activeOpt.key === "showAnnualReports" && (
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() =>
                    typeof onOpenAnnualReports === "function" &&
                    onOpenAnnualReports()
                  }
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0488AF] text-white"
                >
                  Gestionar Cuenta pública (admin)
                </button>
              </div>
            )}

            {activeOpt.key === "showEstatutos" && (
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() =>
                    typeof onOpenStatutes === "function" && onOpenStatutes()
                  }
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0488AF] text-white"
                >
                  Gestionar Estatutos (admin)
                </button>
              </div>
            )}

            {activeOpt.key === "showCodigoEtica" && (
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() =>
                    typeof onOpenEthics === "function" && onOpenEthics()
                  }
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0488AF] text-white"
                >
                  Gestionar Código de ética (admin)
                </button>
              </div>
            )}

            {activeOpt.key === "showModeloPrevencion" && (
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() =>
                    typeof onOpenCrimePrevention === "function" &&
                    onOpenCrimePrevention()
                  }
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0488AF] text-white"
                >
                  Gestionar Modelo de prevención (admin)
                </button>
              </div>
            )}

            {activeOpt.key === "showReglamentoInterno" && (
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() =>
                    typeof onOpenInternalRegulation === "function" &&
                    onOpenInternalRegulation()
                  }
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0488AF] text-white"
                >
                  Gestionar Reglamento interno (admin)
                </button>
              </div>
            )}

            {activeOpt.key === "showProtocolos" && (
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() =>
                    typeof onOpenProtocols === "function" && onOpenProtocols()
                  }
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0488AF] text-white"
                >
                  Gestionar Protocolos (admin)
                </button>
              </div>
            )}

            {activeOpt.key === "showMaterialInteres" && (
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() =>
                    typeof onOpenMaterials === "function" && onOpenMaterials()
                  }
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0488AF] text-white"
                >
                  Gestionar Material de interés (admin)
                </button>
              </div>
            )}

            {activeOpt.key === "showPopups" && (
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() =>
                    typeof onOpenPopups === "function" && onOpenPopups()
                  }
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0488AF] text-white"
                >
                  Gestionar Pop-ups (admin)
                </button>
              </div>
            )}

            {/* Acciones globales - Ocultas para pop-ups ya que no hay configuración que guardar */}
            {activeOpt.key !== "showPopups" && (
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={save}
                  disabled={saving || !hasChanges}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0488AF] text-white disabled:opacity-60"
                >
                  Guardar cambios
                </button>
                <button
                  onClick={reset}
                  disabled={saving || !hasChanges}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border bg-white hover:bg-gray-50 disabled:opacity-60"
                >
                  Descartar cambios
                </button>
              </div>
            )}
          </div>
        </GradientCard>
      </motion.div>
    );

  /* ---------- Grid ---------- */
  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-blue-50 via-white to-rose-50 p-6">
      <div className="max-w-7xl mx-auto">
        {view === null ? (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="space-y-8"
          >
            {/* Header */}
            <Banner
              title="Documentación"
              message="Activa o desactiva lo que verán los socios en el menú DOCUMENTACIÓN OFICIAL."
            />

            {/* Sección de tutorial */}
            <SectionTips
              section="la sección de Documentación Oficial"
              buttonTitle="¿Cómo usar esta sección?"
              tipsTitle="¿Cómo usar la Documentación Oficial?"
              tipsList={[
                'Aquí puedes encontrar consejos o instrucciones para usar la sección de Documentación Oficial.',
                'En esta sección puedes gestionar qué documentos verán los socios en el menú "Documentación Oficial".',
                'El switch "Sección completa" controla la visibilidad general de toda la sección para los socios.',
                'Puedes activar o desactivar cada tipo de documento individualmente mediante los switches.',
                'Cada documento tiene su propia tarjeta de gestión donde puedes subir, editar y organizar el contenido.',
                'Los pop-ups son exclusivamente para administradores y no requieren switch, ya que solo se muestran a usuarios con rol de administrador.',
                'Los cambios que realices se guardan y afectan inmediatamente la visibilidad para los socios.',
              ]}
              color="#0488AF"
              videoUrl="https://res.cloudinary.com/duz01skao/video/upload/v1766415820/Video_Tutorial_m%C3%B3dulo_Administraci%C3%B3n_-_Documentaci%C3%B3n_omokzk.mp4"
              manualUrl="https://res.cloudinary.com/duz01skao/image/upload/v1766412946/Manual_de_usuario_administraci%C3%B3n_bsvsyl.pdf"
            />

            {/* Acciones globales */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={save}
                disabled={loading || saving || !hasChanges}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0488AF] text-white disabled:opacity-60"
              >
                Guardar todo
              </button>
              <button
                onClick={reset}
                disabled={loading || saving || !hasChanges}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border bg-white hover:bg-gray-50 disabled:opacity-60"
              >
                Descartar cambios
              </button>
            </div>

            {/* Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {options.map((opt, index) => {
                const handleCardClick = () => {
                  if (
                    opt.key === "showAnnualReports" &&
                    typeof onOpenAnnualReports === "function"
                  ) {
                    onOpenAnnualReports();
                  } else if (
                    opt.key === "showEstatutos" &&
                    typeof onOpenStatutes === "function"
                  ) {
                    onOpenStatutes();
                  } else if (
                    opt.key === "showCodigoEtica" &&
                    typeof onOpenEthics === "function"
                  ) {
                    onOpenEthics();
                  } else if (
                    opt.key === "showModeloPrevencion" &&
                    typeof onOpenCrimePrevention === "function"
                  ) {
                    onOpenCrimePrevention();
                  } else if (
                    opt.key === "showReglamentoInterno" &&
                    typeof onOpenInternalRegulation === "function"
                  ) {
                    onOpenInternalRegulation();
                  } else if (
                    opt.key === "showProtocolos" &&
                    typeof onOpenProtocols === "function"
                  ) {
                    onOpenProtocols();
                  } else if (
                    opt.key === "showMaterialInteres" &&
                    typeof onOpenMaterials === "function"
                  ) {
                    onOpenMaterials();
                  } else if (
                    opt.key === "showPopups" &&
                    typeof onOpenPopups === "function"
                  ) {
                    onOpenPopups();
                  } else {
                    setView(opt.key);
                  }
                };

                const toggleDisabled =
                  (opt.key !== "docsMenuEnabled" && !cfg.docsMenuEnabled) || loading;

                return (
                  <motion.div
                    key={opt.key}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.08, ease: "easeOut" }}
                    whileHover={{ y: -8, transition: { duration: 0.2 } }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleCardClick}
                    className="group cursor-pointer h-full"
                  >
                    <div className="relative bg-white rounded-2xl p-6 shadow-lg border border-gray-200 hover:shadow-2xl transition-all duration-300 overflow-hidden h-full flex flex-col">
                      <div className="absolute inset-0 bg-gradient-to-br from-[#0488AF]/5 to-[#C42251]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      <div className="relative z-10 space-y-3 flex-1 flex flex-col">
                        <div className="text-3xl">{opt.emoji}</div>
                        <div className="flex items-center justify-between gap-3">
                          <h3 className="text-lg font-bold text-gray-800 group-hover:text-[#0488AF] transition-colors duration-300">
                            {opt.title}
                          </h3>
                          {/* Ocultar toggle para pop-ups */}
                          {opt.key !== "showPopups" && (
                            <Toggle
                              checked={!!cfg[opt.key]}
                              disabled={toggleDisabled}
                              onChange={(v) => setCfg((c) => ({ ...c, [opt.key]: v }))}
                            />
                          )}
                          {opt.key === "showPopups" && (
                            <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full whitespace-nowrap">
                              Solo Admin
                            </span>
                          )}
                        </div>
                        <p className="text-gray-600 text-sm leading-relaxed flex-1">{opt.desc}</p>
                        <div className="mt-auto">
                          {toggleDisabled && opt.key !== "docsMenuEnabled" && opt.key !== "showPopups" && (
                            <div className="text-xs text-gray-500">
                              Requiere activar "Sección completa".
                            </div>
                          )}
                          {opt.key === "showPopups" && (
                            <div className="text-xs text-blue-600">
                              Solo visible para administradores.
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#0488AF] to-[#C42251] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        ) : (
          <Detail />
        )}
      </div>
    </div>
  );
}
